import { useState, useEffect, useRef, useCallback, type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { MDXProvider } from "@mdx-js/react";
import { updates } from "./loadUpdates";
import * as demoComponents from "./demos";
import "./updates-prose.css";

function slugify(text: string) {
	return text
		.toLowerCase()
		.replace(/[^\w\s-]/g, "")
		.replace(/\s+/g, "-");
}

function MdxLink({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
	const navigate = useNavigate();
	const isInternal = href?.startsWith("/");

	if (isInternal) {
		return (
			<a
				{...props}
				href={href}
				onClick={(e) => {
					e.preventDefault();
					navigate(href!);
				}}
			>
				{children}
			</a>
		);
	}

	return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
}

function MdxH2({ children, ...props }: HTMLAttributes<HTMLHeadingElement>) {
	const text = extractText(children);
	const id = slugify(text);
	return <h2 id={id} {...props}>{children}</h2>;
}

function extractText(node: ReactNode): string {
	if (typeof node === "string") return node;
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(extractText).join("");
	if (node && typeof node === "object" && "props" in node) return extractText(node.props.children);
	return "";
}

interface Heading { id: string; text: string }

function useHeadings(contentRef: React.RefObject<HTMLDivElement | null>, slug: string) {
	const [headings, setHeadings] = useState<Heading[]>([]);

	useEffect(() => {
		if (!contentRef.current) return;
		const els = contentRef.current.querySelectorAll("h2[id]");
		setHeadings(Array.from(els).map((el) => ({ id: el.id, text: el.textContent ?? "" })));
	}, [slug, contentRef]);

	return headings;
}

function useActiveHeading(scrollRef: React.RefObject<HTMLElement | null>, headings: Heading[]) {
	const [activeId, setActiveId] = useState<string>("");

	useEffect(() => {
		const container = scrollRef.current;
		if (!container || headings.length === 0) return;

		const onScroll = () => {
			const offset = container.scrollTop + 100;
			let current = headings[0]?.id ?? "";
			for (const h of headings) {
				const el = document.getElementById(h.id);
				if (el && el.offsetTop <= offset) current = h.id;
			}
			setActiveId(current);
		};

		onScroll();
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [scrollRef, headings]);

	return activeId;
}

const mdxComponents = { a: MdxLink, h2: MdxH2, ...demoComponents };

export default function UpdatesView() {
	const navigate = useNavigate();
	const location = useLocation();
	const slugFromUrl = location.pathname.split("/").filter(Boolean)[1] || "";
	const selectedSlug = slugFromUrl || updates[0]?.slug || "";
	const selected = updates.find((u) => u.slug === selectedSlug) ?? updates[0];
	const setSelectedSlug = useCallback((slug: string) => {
		navigate(`/updates/${slug}`, { replace: true });
	}, [navigate]);
	const contentRef = useRef<HTMLDivElement>(null);
	const mainRef = useRef<HTMLElement>(null);
	const headings = useHeadings(contentRef, selected?.slug ?? "");
	const activeId = useActiveHeading(mainRef, headings);

	// Scroll content to top on article change
	useEffect(() => {
		mainRef.current?.scrollTo({ top: 0 });
	}, [selectedSlug]);

	const scrollTo = useCallback((id: string) => {
		const el = document.getElementById(id);
		const container = mainRef.current;
		if (el && container) {
			container.scrollTo({ top: el.offsetTop - 24, behavior: "smooth" });
		}
	}, []);

	if (!selected) return null;

	return (
		<div className="flex w-full h-full overflow-hidden">
			{/* Sidebar */}
			<aside className="w-[220px] min-w-[220px] border-r border-border pt-6 overflow-y-auto">
				<h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4 mb-3">
					Updates
				</h2>
				<nav className="flex flex-col gap-0.5 px-2">
					{updates.map((entry) => (
						<button
							key={entry.slug}
							type="button"
							onClick={() => setSelectedSlug(entry.slug)}
							className={`flex flex-col gap-0.5 px-3 py-2 border-none rounded-md text-left cursor-pointer transition-colors ${
								entry.slug === selected.slug
									? "bg-accent"
									: "bg-transparent hover:bg-accent"
							}`}
						>
							<span className="text-[0.8125rem] font-medium text-foreground">
								{entry.frontmatter.title}
							</span>
							<span className="text-[0.6875rem] text-muted-foreground">
								{format(parseISO(entry.frontmatter.date), "MMMM yyyy")}
							</span>
						</button>
					))}
				</nav>
			</aside>

			{/* Content */}
			<main ref={mainRef} className="flex-1 overflow-y-auto pt-6 px-6">
				<div ref={contentRef} className="max-w-[720px]">
					<span className="text-[0.8125rem] text-muted-foreground">
						{format(parseISO(selected.frontmatter.date), "MMMM d, yyyy")}
					</span>
					<h1 className="text-2xl font-semibold text-foreground mb-6">
						{selected.frontmatter.title}
					</h1>
					<MDXProvider components={mdxComponents}>
						<div className="updates-prose">
							<selected.Component />
						</div>
					</MDXProvider>
				</div>
			</main>

			{/* Table of Contents */}
			{headings.length > 0 && (
				<aside className="w-[200px] min-w-[200px] border-l border-border pt-6 px-4 overflow-y-auto hidden xl:block">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
						On this page
					</h3>
					<nav className="flex flex-col gap-1">
						{headings.map((h) => (
							<button
								key={h.id}
								type="button"
								onClick={() => scrollTo(h.id)}
								className={`text-left text-[0.75rem] leading-snug px-2 py-1 rounded border-none cursor-pointer transition-colors ${
									activeId === h.id
										? "text-primary font-medium bg-primary/5"
										: "text-muted-foreground bg-transparent hover:text-foreground"
								}`}
							>
								{h.text}
							</button>
						))}
					</nav>
				</aside>
			)}
		</div>
	);
}
